import React from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Switch,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import useMeta from "../../base/hooks/useMeta";
import useForm from "../../base/hooks/form/useForm";
import useFormActions from "../../base/hooks/form/useFormActions";
import {
    MUTATION_UPDATE_BLAZE_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import { MUTATION_SYNC_FROM_BLAZE } from "../../apollo/module-operations/blaze";
import { useMutation } from "@apollo/react-hooks";
import useValidator from "../../base/hooks/form/useValidator";
import useNotification from "../../base/hooks/components/useNotification";
import _ from "lodash";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const SyncIcon = () => <Icon icon="refresh" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const BlazeForm = ({ app }) => {
    const notify = useNotification();

    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                id: inputs.id,
                sandbox: inputs.sandbox,
                auth_key: inputs.auth_key,
                dev_auth_key: inputs.dev_auth_key,
                partner_key: inputs.partner_key,
                dev_partner_key: inputs.dev_partner_key,
                type: "Blaze",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "id",
                method: "isEmpty",
                validWhen: false,
                message: "This app id is required.",
            },

            {
                field: "auth_key",
                method: "isEmpty",
                validWhen: false,
                message: "This auth key is required.",
            },

            {
                field: "dev_auth_key",
                method: "isEmpty",
                validWhen: false,
                message: "This dev auth key id is required.",
            },
            {
                field: "partner_key",
                method: "isEmpty",
                validWhen: false,
                message: "This partner key id is required.",
            },
            {
                field: "dev_partner_key",
                method: "isEmpty",
                validWhen: false,
                message: "This dev partner key id is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Blaze",
        "",
        MUTATION_UPDATE_BLAZE_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const [mutateSyncFromBlaze, { loading: syncLoading }] = useMutation(
        MUTATION_SYNC_FROM_BLAZE,
        {
            onCompleted: () => {
                notify("Sync Product From Blaze Successfully", "success");
            },
            onError: () => {
                notify("Error Trying to Sync Product", "error");
            },
        },
    );

    const syncProducstFromBlaze = async () => {
        mutateSyncFromBlaze({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
            },
        });
    };

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Blaze</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Blaze Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "sync-products",
                                label: "Sync Products",
                                icon: syncLoading ? undefined : <SyncIcon />,
                                loading: syncLoading,
                                disabled: syncLoading,
                                variant: "secondary",
                                onClick: syncProducstFromBlaze,
                            },
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Blaze actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Information">
                        <Switch
                            checked={Boolean(inputs.sandbox)}
                            label="Sandbox"
                            name="sandbox"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, "auth_key")}
                                fullWidth
                                label="Auth Key"
                                name="auth_key"
                                onChange={inputChange}
                                value={inputs.auth_key || ""}
                            />
                            <Input
                                error={fieldError(validator, "dev_auth_key")}
                                fullWidth
                                label="Dev auth key"
                                name="dev_auth_key"
                                onChange={inputChange}
                                value={inputs.dev_auth_key || ""}
                            />
                            <Input
                                error={fieldError(validator, "partner_key")}
                                fullWidth
                                label="Parther key"
                                name="partner_key"
                                onChange={inputChange}
                                value={inputs.partner_key || ""}
                            />
                            <Input
                                error={fieldError(validator, "dev_partner_key")}
                                fullWidth
                                label="Dev Parther key"
                                name="dev_partner_key"
                                onChange={inputChange}
                                value={inputs.dev_partner_key || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default BlazeForm;
